import React, { useState } from 'react';
import AudioPlayer from './AudioPlayer/AudioPlayer.tsx';
import ImageViewer from './ImageViewer/ImageViewer.tsx';
import TextViewer from './TextViewer/TextViewer.tsx';
import VideoPlayer from './VideoPlayer/VideoPlayer.tsx';
import PdfViewer from './PdfViewer/PdfViewer.tsx';
import DocxViewer from './DocxViewer/DocxViewer.tsx';
import { useAppStore } from "../../../GlobalStore/GlobalStore.ts";
import { downloadContent } from "../../../Utils/downloadUtils/DownloadUtils.ts";

interface EnlargedContentProps {}

const EnlargedContent: React.FC<EnlargedContentProps> = () => {
    const dynamicStateObj = useAppStore((state: any) => state.dynamicStateObj);
    const {enlargedContent} = dynamicStateObj;
    const setDynamicStateObj = useAppStore((state: any) => state.setDynamicStateObj);
    const [downloadError, setDownloadError] = useState<string | null>(null);
    const [isDownloading, setIsDownloading] = useState(false);

    const handleClose = () => {
        setDynamicStateObj({visiblePopup: null, enlargedContent: null});
        setDownloadError(null);
    };

    React.useEffect(() => {
        if (!enlargedContent) return;

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                handleClose();
            }
        };

        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [enlargedContent]);

    // Helper function to get filename based on content type
    const getFileName = (type: string): string => {
        if (enlargedContent.filename) return enlargedContent.filename;

        switch (type) {
            case 'file':
                if (enlargedContent.docType === 'pdf') return 'document.pdf';
                if (enlargedContent.docType === 'docx') return 'document.docx';
                return 'document.txt';
            case 'image':
                return 'image.png';
            case 'gif':
                return 'animation.gif';
            case 'audio':
                return 'audio.mp3';
            case 'video':
                return 'video.mp4';
            case 'document':
                switch(enlargedContent.docType) {
                    case 'pdf':
                        return 'document.pdf';
                    case 'docx':
                        return 'document.docx';
                    default:
                        return 'document.pdf';
                }
            case 'txt':
                return 'document.txt';
            default:
                return 'download';
        }
    };

    // Helper function to get file type for download utility
    const getFileType = (type: string): 'svg' | 'xlsx' | 'png' | 'txt' | 'pdf' | 'docx' | 'json' => {
        switch (type) {
            case 'document':
                return enlargedContent.docType === 'docx' ? 'docx' : 'pdf';
            case 'file':
                if (enlargedContent.docType === 'pdf') return 'pdf';
                if (enlargedContent.docType === 'docx') return 'docx';
                return 'txt';
            case 'image':
                return 'png';
            case 'gif':
                return 'png';
            case 'audio':
                return 'txt'; // Audio files will be handled as binary
            case 'video':
                return 'txt'; // Video files will be handled as binary
            case 'txt':
                return 'txt';
            default:
                return 'txt';
        }
    };

    const handleDownload = async (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();

        if (isDownloading) return;

        try {
            setIsDownloading(true);
            setDownloadError(null);

            await downloadContent({
                content: enlargedContent.content,
                filename: getFileName(enlargedContent.type),
                type: getFileType(enlargedContent.type)
            });

        } catch (error) {
            console.error('Download error:', error);
            setDownloadError('Failed to download the file. Please try again later.');
        } finally {
            setIsDownloading(false);
        }
    };

    if (!enlargedContent) return null;

    const isImageContent = enlargedContent.type === "image" || enlargedContent.type === "gif";
    const shellClassName = isImageContent
        ? "relative h-[min(88vh,960px)] w-[min(94vw,1400px)] overflow-hidden rounded-[1.75rem]"
        : "relative";

    return (
        <div
            className="fixed inset-0 flex items-center justify-center bg-neutral-950 px-4 py-6 z-50"
            onClick={handleClose}
        >
            <div className={shellClassName} onClick={e => e.stopPropagation()}>
                {enlargedContent.type === "audio" && (
                    <AudioPlayer src={enlargedContent.content}/>
                )}
                {(enlargedContent.type === "file" || enlargedContent.type === "link") && (
                    <TextViewer content={enlargedContent.content}/>
                )}
                {enlargedContent.type === "document" && (
                    <>
                        {enlargedContent.docType === 'pdf' && (
                            <PdfViewer src={enlargedContent.content}/>
                        )}
                        {enlargedContent.docType === 'docx' && (
                            <DocxViewer src={enlargedContent.content}/>
                        )}
                    </>
                )}
                {(enlargedContent.type === "image" || enlargedContent.type === "gif") && (
                    <ImageViewer
                        src={enlargedContent.content}
                        alt={enlargedContent.alt}
                    />
                )}
                {enlargedContent.type === "video" && (
                    <VideoPlayer
                        src={enlargedContent.content}
                        previewUrl={enlargedContent.previewUrl}
                    />
                )}
                <div
                    className="absolute right-3 top-3 flex gap-2 z-10"
                >
                    <button
                        onClick={handleDownload}
                        className={`rounded-full bg-neutral-800/80 p-2 text-white transition-opacity ${isDownloading ? 'opacity-50 cursor-default' : 'hover:bg-neutral-700'}`}
                        disabled={isDownloading}
                        type="button"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none"
                             viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                        </svg>
                    </button>
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleClose();
                        }}
                        className="rounded-full bg-neutral-800/80 p-2 text-white transition-colors hover:bg-neutral-700"
                        type="button"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none"
                             viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                    </button>
                </div>
            </div>
            {isDownloading && (
                <div
                    className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-neutral-800 text-white px-4 py-2 rounded">
                    <div className="text-center">Downloading...</div>
                </div>
            )}
            {downloadError && (
                <div
                    className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-4 py-2 rounded">
                    {downloadError}
                </div>
            )}
        </div>
    );
}

export default EnlargedContent;
